import { MoreIcon } from '@persona-id/design-tokens/icons';
import { keyBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom-v5-compat';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import TableLayout, {
  TableActionTD,
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { CaseTemplate } from 'dashboard/models/case-template';
import { Workflow, WorkflowStatus } from 'dashboard/models/workflow';
import {
  WorkflowDeployment,
  WorkflowDeploymentConfigurationLabel,
} from 'dashboard/models/workflow-deployment';
import CaseActionStatusSticker from 'dashboard/views/DashboardCaseActionsView/CaseActionStatusSticker';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import NullStateCaseActions from 'images/dashboard/i16-briefcase.svg';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useConfirmation from 'lib/hooks/useConfirmation';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';

interface CaseActionsTableProps {
  actionWorkflows: Workflow[];
  caseTemplates: CaseTemplate[];
  workflowDeployments: WorkflowDeployment[];
  hasUsedFilters: boolean;
  isLoading: boolean;
  archiveActionWorkflow: (workflow: Workflow) => Promise<void>;
}

const CaseActionsTable = ({
  actionWorkflows,
  workflowDeployments,
  caseTemplates,
  hasUsedFilters,
  isLoading,
  archiveActionWorkflow,
}: CaseActionsTableProps) => {
  const [showArchiveActionWorkflowModal, { prompt, confirm, cancel }] = useConfirmation();
  const workflowDeploymentMap = keyBy('id', workflowDeployments);
  const navigate = useNavigate();

  const hasCaseActionViewPermission = usePermission(Permission.CaseActionView);
  const hasCaseActionWritePermission = usePermission(Permission.CaseActionWrite);

  const caseActionRows = actionWorkflows.map((workflow: Workflow) => {
    const {
      id: workflowId,
      attributes: { name, status, 'updated-at': updatedAt },
      relationships: {
        'latest-published-version': latestPublishedVersion,
        'latest-draft-version': latestDraftVersion,
        'active-deployment': activeDeployment,
      },
    } = workflow;

    const handleClickRuns = () => {
      navigate(`/dashboard/case-actions/${workflowId}/runs`);
    };

    const workflowVersionId =
      latestDraftVersion.data?.id ??
      workflowDeploymentMap[activeDeployment.data?.id]?.attributes['configuration']?.versions?.find(
        (version) => version.label === WorkflowDeploymentConfigurationLabel.Treatment,
      )?.token ??
      latestPublishedVersion.data?.id;

    const caseTemplateNames =
      (workflow.relationships['case-templates'].data || [])
        .map((template) => {
          return caseTemplates.find((caseTemplate) => caseTemplate.id === template.id)?.attributes
            .name;
        })
        .join(', ') || '-';

    const updatedAtDisplay = updatedAt
      ? DateTime.fromISO(updatedAt).toFormat(DATE_AND_TIME_FORMAT)
      : '–';

    const handleArchiveCaseActionClick = () => prompt(() => archiveActionWorkflow(workflow));

    return (
      <TableLinkTR
        to={`/dashboard/case-actions/${workflow.id}/${workflowVersionId}`}
        data-test={`case-actions-table__row--${workflow.id}`}
        id={`case-actions-table__row--${workflow.id}`}
        key={workflow.id}
      >
        <TableTD>{name}</TableTD>
        <TableTD>
          <CaseActionStatusSticker status={status} />
        </TableTD>
        <TableTD>{caseTemplateNames}</TableTD>
        <TableTD>{updatedAtDisplay}</TableTD>
        <TableActionTD>
          <DropdownMenu
            trigger={
              <Button
                aria-label={`More actions for ${workflow.id}`}
                id={`case-actions-table-row__action-menu--${workflow.id}`}
                variant='inverse'
                width='icon'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <MoreIcon />
              </Button>
            }
          >
            <DropdownMenu.Item
              key='runs'
              id={`action-menu__runs--${workflow.id}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClickRuns();
              }}
              disabled={!hasCaseActionViewPermission}
            >
              Runs
            </DropdownMenu.Item>
            {status !== WorkflowStatus.Archived && (
              <DropdownMenu.Item
                key='archive'
                variant='danger'
                id={`action-menu__archive--${workflow.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleArchiveCaseActionClick();
                }}
                disabled={!hasCaseActionWritePermission}
              >
                Archive
              </DropdownMenu.Item>
            )}
          </DropdownMenu>
        </TableActionTD>
      </TableLinkTR>
    );
  });

  return (
    <>
      <TableLayout
        dataTest='case-actions-table'
        isLoading={isLoading}
        showHeader={actionWorkflows.length !== 0}
        isEmpty={actionWorkflows.length === 0}
        nullState={
          <TableNullState
            hasUsedFilters={hasUsedFilters}
            dataTestBlock='case-actions-table__null-state'
          >
            <NullStateCaseActions />
            <Spacer height={3} />
            <div>
              Cases Actions are one-click macros that enable your team to automatically run an
              action for a given case.
            </div>
            <Spacer height={5} />
            <a
              href='https://support.withpersona.com/hc/en-us/articles/5774868825875-Case-Actions'
              target='_blank'
              rel='noreferrer'
            >
              Learn more about case actions
            </a>
          </TableNullState>
        }
        headerTR={
          <>
            <TableTH>Name</TableTH>
            <TableTH>Status</TableTH>
            <TableTH>Used in</TableTH>
            <TableTH>
              Last updated at ({DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})
            </TableTH>
            <TableTH>{/* Actions column */}</TableTH>
          </>
        }
      >
        {caseActionRows}
      </TableLayout>
      <ModalWrapper isOpen={showArchiveActionWorkflowModal} handleCloseModal={cancel}>
        <ConfirmationModal
          cancelAction={cancel}
          confirmAction={confirm}
          header='Archive this case action?'
          confirmActionText='Archive'
        >
          <p>
            This action is permanent and can’t be undone. You will no longer be able to run this
            case action.
          </p>
        </ConfirmationModal>
      </ModalWrapper>
    </>
  );
};

export default CaseActionsTable;
